// The smallest store that works with useSyncExternalStore: display reads
// state and sends intents to systems; systems replace state immutably.
import { useSyncExternalStore } from 'react';

export interface Store<T> {
  get(): T;
  set(next: T | ((current: T) => T)): void;
  subscribe(listener: () => void): () => void;
}

export function createStore<T>(initial: T): Store<T> {
  let state = initial;
  const listeners = new Set<() => void>();
  return {
    get: () => state,
    set(next) {
      const value = typeof next === 'function' ? (next as (current: T) => T)(state) : next;
      if (Object.is(value, state)) return;
      state = value;
      for (const l of listeners) l();
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

export function useStore<T>(store: Store<T>): T {
  return useSyncExternalStore(store.subscribe, store.get, store.get);
}

/** One value from a store, re-rendering only when that value changes; select must return a primitive or a stable reference. */
export function useStoreSelect<T, U>(store: Store<T>, select: (state: T) => U): U {
  return useSyncExternalStore(store.subscribe, () => select(store.get()), () => select(store.get()));
}
